import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, TrendingDownIcon, HourglassIcon, UserMinusIcon, ShieldAlertIcon, ZapIcon, GaugeIcon, SparklesIcon, ShieldCheckIcon } from 'lucide-react';
import { Container } from '../components/Container';
const today = [{
  icon: TrendingDownIcon,
  label: "Staffing costs that don't scale"
}, {
  icon: HourglassIcon,
  label: 'Delayed revenue recognition'
}, {
  icon: UserMinusIcon,
  label: 'Poor producer experience'
}, {
  icon: ShieldAlertIcon,
  label: 'E&O exposure & compliance risk'
}];
const withPigeon = [{
  icon: ZapIcon,
  label: 'Automation that scales'
}, {
  icon: GaugeIcon,
  label: 'Faster cycle times'
}, {
  icon: SparklesIcon,
  label: 'Best-in-class producer experience'
}, {
  icon: ShieldCheckIcon,
  label: 'Always audit-ready'
}];
export function TodayVsPigeon() {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsDesktop(mql.matches);
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: headerRef,
    offset: ['start end', 'end center']
  });
  const scale = useTransform(scrollYProgress, [0, 0.85], [1.25, 1]);
  const line2Opacity = useTransform(scrollYProgress, [0.25, 0.65], [0, 1]);
  return <section className="relative border-b border-white/5 bg-zinc-950 py-12 sm:py-20" data-id="element-888">
      <Container data-id="element-889">
        {/* Header */}
        <div ref={headerRef} className="mx-auto mb-10 max-w-3xl text-center sm:mb-12" data-id="element-890">
          <motion.h2 style={isDesktop ? {
          scale
        } : undefined} className="display-tighten text-balance origin-center font-serif-display text-[2.85rem] leading-[1.02] text-white sm:text-5xl sm:leading-[1.0] lg:text-6xl" data-id="element-891">
            From inbox chaos
            <br data-id="element-892" />
            <motion.span style={{
            opacity: line2Opacity
          }} className="inline-block" data-id="element-893">
              to{' '}
              <span className="italic text-accent" data-id="element-894">operational leverage.</span>
            </motion.span>
          </motion.h2>
          <p className="mt-5 text-sm text-zinc-400 sm:text-base" data-id="element-895">
            Same workflows. Without the manual chase.
          </p>
        </div>

        {/* Comparison cards — compact */}
        <div className="grid items-stretch gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.04] shadow-[0_24px_48px_-24px_rgba(0,0,0,0.6)] lg:grid-cols-[1fr_auto_1fr]" data-id="element-896">
          {/* Today */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '400px'
        }} transition={{
          duration: 0.5
        }} className="bg-zinc-950 p-6 sm:p-8" data-id="element-897">
            <div className="eyebrow text-zinc-500" data-id="element-898">Today</div>
            <h3 className="mt-3 font-serif-display text-[2.5rem] leading-[1.02] tracking-tight text-white sm:text-3xl sm:leading-[1.05]" data-id="element-899">
              Paper chased{' '}
              <span className="italic text-rose-400/90" data-id="element-900">by hand.</span>
            </h3>
            <ul className="mt-5 space-y-1.5" data-id="element-901">
              {today.map(t => <li key={t.label} className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-sm text-zinc-300" data-id="element-902">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.03]" data-id="element-903">
                    <t.icon className="h-3 w-3 text-zinc-400" data-id="element-904" />
                  </span>
                  {t.label}
                </li>)}
            </ul>
          </motion.div>

          {/* Connector — horizontal arrow on desktop, vertical down-arrow on mobile to preserve the "from → to" narrative */}
          <div className="flex items-center justify-center bg-zinc-950 py-3 lg:px-5 lg:py-0" data-id="element-905">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/30 bg-zinc-900" data-id="element-906">
              <ArrowRightIcon className="h-4 w-4 rotate-90 text-accent lg:rotate-0" data-id="element-907" />
            </div>
          </div>

          {/* With Pigeon */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '400px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative overflow-hidden bg-zinc-950 p-6 sm:p-8" data-id="element-908">
            {/* Flowing blue shader backdrop */}
            <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-909">
              <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 600 400" data-id="element-910">
                <defs data-id="element-911">
                  <linearGradient id="blue-shader" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-912">
                    <stop offset="0%" stopColor="#1e3a8a" data-id="element-913" />
                    <stop offset="25%" stopColor="#2563eb" data-id="element-914" />
                    <stop offset="50%" stopColor="#38bdf8" data-id="element-915" />
                    <stop offset="75%" stopColor="#67e8f9" data-id="element-916" />
                    <stop offset="100%" stopColor="#1e40af" data-id="element-917" />
                  </linearGradient>
                  <filter id="warp" x="-20%" y="-20%" width="140%" height="140%" data-id="element-918">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01 0.014" numOctaves="2" seed="4" result="noise" data-id="element-919">
                      <animate attributeName="baseFrequency" dur="24s" values="0.01 0.014; 0.014 0.008; 0.01 0.014" repeatCount="indefinite" data-id="element-920" />
                    </feTurbulence>
                    <feDisplacementMap in="SourceGraphic" in2="noise" scale="100" data-id="element-921" />
                  </filter>
                </defs>
                <g filter="url(#warp)" data-id="element-922">
                  <rect x="-100" y="-100" width="800" height="600" fill="url(#blue-shader)" data-id="element-923" />
                </g>
              </svg>
              <div className="absolute inset-0 bg-zinc-950/65" data-id="element-924" />
              <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/[0.06] to-transparent" data-id="element-925" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(9,9,11,0.6)_100%)]" data-id="element-926" />
              <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
            }} data-id="element-927" />
            </div>

            <div className="relative" data-id="element-928">
              <div className="flex items-center gap-1.5" data-id="element-929">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Ti5Jr9Zm1LRNz51qTKsea/1.png" alt="" aria-hidden className="h-5 w-auto" data-id="element-930" />
                <span className="text-base font-semibold leading-none tracking-tight text-white" data-id="element-931">
                  Pigeon
                </span>
              </div>
              <h3 className="mt-3 font-serif-display text-[2.5rem] leading-[1.02] tracking-tight text-white sm:text-3xl sm:leading-[1.05]" data-id="element-932">
                Paper chased{' '}
                <span className="italic text-accent" data-id="element-933">automatically.</span>
              </h3>
              <ul className="mt-5 space-y-1.5" data-id="element-934">
                {withPigeon.map(w => <li key={w.label} className="flex items-center gap-2.5 rounded-lg border border-white/[0.10] bg-zinc-950/55 px-3 py-2 text-sm font-medium text-white shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] backdrop-blur-xl" data-id="element-935">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-accent/30 bg-accent/15" data-id="element-936">
                      <w.icon className="h-3 w-3 text-accent" data-id="element-937" />
                    </span>
                    {w.label}
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>;
}